/**
 * Mobile-Navigation: Bottom-Tab-Bar + Volltext-Menue-Sheet.
 *
 * Wird ausschliesslich unter dem `md:`-Breakpoint sichtbar. Auf Desktop
 * (>= md) bleiben Sidebar und Layout exakt wie bisher — diese Komponente
 * rendert nur Elemente mit `md:hidden`-Klasse oder per `if (isMobile)`-
 * Logik, die auf Desktop nichts produziert.
 *
 * Tabs (5 Stueck, iOS-typisch):
 *   - Start:       /
 *   - Rechnungen:  /orderbuch
 *   - Kunden:      /kunden
 *   - Suche:       oeffnet SearchDialog
 *   - Menue:       oeffnet MobileMenu-Sheet mit allen weiteren Routen
 *
 * Layout-Notiz: BottomNav ist `fixed bottom-0`, nutzt
 * `env(safe-area-inset-bottom)` damit der iPhone-Home-Indicator nicht
 * ueber die Tabs streicht. main-Content bekommt im Layout `pb-16 md:pb-0`
 * damit die untersten Eintraege nicht von der TabBar verdeckt werden.
 */
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Building2, ChevronRight, FileText, LayoutDashboard, Menu, Search, X } from 'lucide-react';
import { cn, initials } from '@/lib/utils';

interface PrimaryTab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const PRIMARY_TABS: PrimaryTab[] = [
  { to: '/', label: 'Start', icon: LayoutDashboard },
  { to: '/orderbuch', label: 'Rechnungen', icon: FileText },
  { to: '/kunden', label: 'Kunden', icon: Building2 },
];

export function MobileNav({
  onSearchClick,
  onMenuClick,
}: {
  onSearchClick: () => void;
  onMenuClick: () => void;
}) {
  return (
    <nav
      className={cn(
        'md:hidden fixed bottom-0 left-0 right-0 z-30',
        'border-t border-hair bg-panel',
        // Safe-Area-Inset nur auf iOS-Devices wirksam, sonst 0
        'pb-[env(safe-area-inset-bottom)]',
      )}
      aria-label="Mobile-Navigation"
    >
      <div className="flex items-stretch justify-around">
        {PRIMARY_TABS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px]',
                isActive ? 'text-avameo-primary' : 'text-ink-muted',
              )
            }
          >
            <Icon className="h-5 w-5" />
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={onSearchClick}
          className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] text-ink-muted active:text-avameo-primary"
          aria-label="Suche oeffnen"
        >
          <Search className="h-5 w-5" />
          <span>Suche</span>
        </button>
        <button
          type="button"
          onClick={onMenuClick}
          className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] text-ink-muted active:text-avameo-primary"
          aria-label="Menue oeffnen"
        >
          <Menu className="h-5 w-5" />
          <span>Menü</span>
        </button>
      </div>
    </nav>
  );
}

/**
 * Vollbild-Sheet mit den uebrigen Nav-Eintraegen, User-Info und Theme-Toggle.
 * Die 3 PRIMARY_TABS aus der BottomNav werden hier nicht doppelt angezeigt.
 */
export function MobileMenu({
  open,
  onOpenChange,
  sections,
  userName,
  userRole,
  onLogout,
  onThemeToggle,
  themeIcon,
  themeLabel,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  sections: Array<{
    section: string;
    items: Array<{ to: string; label: string; icon: React.ComponentType<{ className?: string }> }>;
  }>;
  userName: string;
  userRole: string;
  onLogout: () => void;
  onThemeToggle: () => void;
  themeIcon: React.ReactNode;
  themeLabel: string;
}) {
  // Body-Scroll sperren, solange Sheet offen
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc schliesst Sheet (Tastatur-User auf Tablet/Desktop wenn Devtools auf
  // Mobile-Breakpoint stehen)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  if (!open) return null;

  // Filter aus den Sections die 3 Primary-Tab-Routen raus, die zeigen
  // wir hier nicht nochmal — sind ja schon in der BottomNav.
  const primaryRoutes = new Set(PRIMARY_TABS.map((t) => t.to));
  const filteredSections = sections
    .map((s) => ({
      section: s.section,
      items: s.items.filter((i) => !primaryRoutes.has(i.to)),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <div
      className="md:hidden fixed inset-0 z-50 flex flex-col bg-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Hauptmenue"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-hair px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="text-sm font-medium">Menü</div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-muted active:bg-bg-alt"
          aria-label="Menue schliessen"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav-Eintraege */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {filteredSections.map(({ section, items }) => (
          <div key={section} className="mb-4 last:mb-0">
            {section && (
              <div className="mb-1 px-3 pt-2 text-[10px] font-medium uppercase tracking-wider text-ink-muted">
                {section}
              </div>
            )}
            <div className="space-y-0.5">
              {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => onOpenChange(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-3 text-[14px]',
                      isActive
                        ? 'bg-avameo-primary/5 text-avameo-primary'
                        : 'text-ink-soft active:bg-bg-alt',
                    )
                  }
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* User-Info + Theme + Logout am unteren Rand */}
      <div className="border-t border-hair px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="text-[10px] uppercase tracking-wider text-ink-muted">Angemeldet</div>
        {/* Name führt auf /konto (Passwort, Zwei-Faktor, Verbindungen) —
            Parität zum Desktop-Sidebar-Block. BL-113: Chevron statt reiner
            Active-Textfarbe, sonst liest sich der Name nicht als Link. */}
        <NavLink
          to="/konto"
          onClick={() => onOpenChange(false)}
          className="-mx-1.5 mt-1 flex items-center gap-1.5 truncate rounded-md px-1.5 py-1 text-[14px] text-ink active:bg-bg-alt active:text-avameo-primary"
        >
          {/* BL-150: dasselbe Initialen-Avatar wie Desktop-Sidebar/Top-Bar
              statt eines generischen Personen-Icons. */}
          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-avameo-primary text-[9px] font-semibold text-white">
            {initials(userName)}
          </span>
          <span className="flex-1 truncate">{userName}</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
        </NavLink>
        <div className="truncate text-[12px] text-ink-muted">
          {userRole}
          {' · avameo GmbH'}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onLogout}
            className="flex-1 border border-hair-strong bg-transparent px-3 py-2 text-[13px] text-ink-soft active:bg-bg-alt"
          >
            Abmelden
          </button>
          <button
            type="button"
            onClick={onThemeToggle}
            className="inline-flex items-center gap-1.5 border border-hair-strong bg-transparent px-3 py-2 text-[13px] text-ink-soft active:bg-bg-alt"
            aria-label={themeLabel}
          >
            {themeIcon}
            {themeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
